import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { uploadData } from 'aws-amplify/storage';
import { ConfigurationService } from 'src/app/modules/configuration/services/configuration/configuration.service';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { AuthSessionService } from 'src/app/modules/auth/services/auth-session/auth-session.service';
import { FeatureFlags } from '../../enums/feature-flags';
import awsmobile from 'src/aws-exports';
import { BehaviorSubject } from 'rxjs';

/**
 * Base interface for loggable data - extend this for specific log types
 */
export interface LogData {
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | LogData
    | LogData[]
    | object;
}

/**
 * Interface for logging session data
 */
export interface LogInfoData {
  /** Timestamp when the log entry was created */
  timestamp: number;
  /** Type/category of the log entry */
  type: string;
  /** User oid */
  userId?: string;
  /** Amira Activity ID */
  amiraActivityId?: string;
  /** Amira Assignment ID */
  amiraAssignmentId?: string;
  /** Amira Activity UUID */
  activityUuid?: string;
  /** The actual data payload being logged */
  data: LogData;
  /** Browser user agent string */
  userAgent?: string;
  /** Current URL when the log was created */
  url?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  inboxActivityId = new BehaviorSubject<string>('');
  inboxAssignmentId = new BehaviorSubject<string>('');
  userId = new BehaviorSubject<string>('');
  activityUuid = new BehaviorSubject<string>('');

  constructor(
    private configService: ConfigurationService,
    private featureFlagService: FeatureFlagService,
    private authSessionService: AuthSessionService
  ) {
    // Configure AWS Amplify - this ensures Storage has the correct configuration
    Amplify.configure(awsmobile);
  }

  /**
   * Logs session information to AWS S3 bucket using Amplify Storage uploadData (current API)
   * Creates a structured LogSessionData object with metadata and the provided data.
   *
   * @param infoType The type/category of the log entry
   * @param logData The data to be logged (must conform to LoggableData interface)
   * @param logKeyOverride Optional override for the log key. logKey is usually amiraActivityId or userId
   * @returns Promise<boolean> indicating success/failure of the logging operation
   */
  async logInfo(
    infoType: string,
    logData: LogData,
    logKeyOverride?: string
  ): Promise<boolean> {
    // Check if logging is enabled via feature flag
    if (
      !this.featureFlagService.isFlagEnabled(
        FeatureFlags.ENABLE_LOGGING_SERVICE
      ) ||
      this.authSessionService.isTeacher
    ) {
      return Promise.resolve(false);
    }

    if (!this.inboxActivityId.value && !this.userId.value) {
      console.log('LoggingService: No activity ID or user ID found');
      return Promise.resolve(false);
    }

    // Check if bucket is configured
    if (!this.configService.config.loggingBucket) {
      console.error('Logging bucket not configured in application settings');
      return Promise.resolve(false);
    }

    try {
      const logKey = this.generateLogKey(logKeyOverride);
      const body = this.createLogInfoData(infoType, logData);
      const filePath = this.createFilePath(infoType, logKey);

      const result = await uploadData({
        path: filePath,
        data: JSON.stringify(body),
        options: {
          contentType: 'application/json',
          bucket: {
            bucketName: this.configService.config.loggingBucket,
            region: awsmobile.aws_cognito_region,
          },
        },
      }).result;

      if (!result) {
        console.error(
          'LoggingService: Failed to log session info for unknown reason'
        );
        return Promise.resolve(false);
      }

      return Promise.resolve(true);
    } catch (err) {
      this.handleLoggingError(err);
      return Promise.resolve(false);
    }
  }

  private createFilePath(infoType: string, logKey: string): string {
    const fileName = `${Date.now()}_${infoType}.json`;
    return `${logKey}/${fileName}`;
  }

  /*
   * Creates a structured log data object
   * @param infoType The type/category of the log entry
   * @param logData The data to be logged (must conform to LoggableData interface)
   * @returns The structured log data object
   */
  private createLogInfoData(infoType: string, logData: LogData): LogInfoData {
    return {
      timestamp: Date.now(),
      type: infoType,
      userId: this.userId.value,
      amiraActivityId: this.inboxActivityId.value,
      amiraAssignmentId: this.inboxAssignmentId.value,
      activityUuid: this.activityUuid.value,
      userAgent: navigator.userAgent,
      url: window.location.href,
      data: logData,
    };
  }

  /*
   * Handles logging errors
   * @param err The error to handle
   */
  private handleLoggingError(err: unknown): void {
    if (!(err instanceof Error)) {
      console.error('LoggingService: Failed to log session info:', err);
      return;
    }

    const errorHandlers = [
      {
        condition: (msg: string): boolean =>
          msg.includes('Network Error') || msg.includes('NetworkError'),
        message:
          'LoggingService: Network error - check internet connection and AWS configuration',
      },
      {
        condition: (msg: string): boolean =>
          msg.includes('Access Denied') || msg.includes('403'),
        message:
          'LoggingService: Access denied - check AWS credentials and bucket permissions',
      },
      {
        condition: (msg: string): boolean => msg.includes('timeout'),
        message:
          'LoggingService: Request timed out - check network connectivity',
      },
    ];

    const handler = errorHandlers.find(h => h.condition(err.message));
    if (handler) {
      console.error(handler.message);
    } else {
      console.error('LoggingService: Failed to log session info:', err);
    }
  }

  /*
   * Generates a log key based on the override or the inbox activity ID or user ID
   * @param logKeyOverride Optional override for the log key. logKey is usually amiraActivityId or userId
   * @returns The log key
   */
  private generateLogKey(logKeyOverride?: string): string {
    if (logKeyOverride) return logKeyOverride;
    if (this.inboxActivityId.value)
      return this.uuidToHEX(this.inboxActivityId.value);
    return this.userId.value;
  }

  /**
   * Converts UUID to hexadecimal string
   * @param uuid The UUID string to convert
   * @returns Hexadecimal representation of the UUID
   */
  private uuidToHEX(uuid: string): string {
    // Remove hyphens and convert to hex
    return uuid.replace(/-/g, '').toUpperCase();
  }
}
